import {
  type FrameEditableProps,
  type InstanceEditableProps,
} from '../../editor/commandBuilders.js';
import { DEG2RAD, TWO_PI } from '../../math/angle.js';
import type { Scene, SceneNodeId } from '../../scene/sceneState.js';
import type { AppState } from '../../state/appState.js';
import {
  createCheckboxField,
  createNumberTupleInput,
  createNumberInput,
  createPropRow,
  createSectionHeader,
  type SceneTreeWidgetDeps,
} from './widgets.js';
import {
  findParentFrameId,
  frameLayoutDirection,
} from './queries.js';
import {
  setFramePropertyAndRender,
  setInstancePropertyAndRender,
  type SceneTreePropertyActionDeps,
} from './propertyActions.js';

const VEC2_COMPONENTS = [
  { index: 0, title: 'X' },
  { index: 1, title: 'Y' },
] as const;

export function renderNodeTransformProps(
  deps: SceneTreePropertyActionDeps & SceneTreeWidgetDeps & { appState: AppState },
  panel: HTMLElement,
  node: (
    FrameEditableProps
    | InstanceEditableProps
  ) & { id: SceneNodeId; kind: 'frame' | 'instance' | 'layer'; visible: boolean },
  activeScene: Scene,
): void {
  panel.appendChild(createSectionHeader('transform'));

  const parentLayout = (() => {
    const pid = findParentFrameId(activeScene, node.id);
    const pf = pid ? activeScene.nodes[pid] : null;
    if (pf?.kind !== 'frame') return null;
    return frameLayoutDirection(deps.appState, pf.layout);
  })();
  const canPositionFreely = node.kind === 'frame' || parentLayout === null;

  if (canPositionFreely) {
    const positionRow = createPropRow('position');
    const setPosition = (value: typeof node.position) => {
      if (node.kind === 'frame') setFramePropertyAndRender(deps, node.id, 'position', value);
      else setInstancePropertyAndRender(deps, node.id, 'position', value);
    };
    positionRow.appendChild(createNumberTupleInput(deps, node.position, VEC2_COMPONENTS, setPosition, {
      min: -9999,
      max: 9999,
      step: 1,
      historyLabel: `Set ${node.kind} position`,
      visual: { subtype: 'pixels' },
    }));
    panel.appendChild(positionRow);
  }

  const rotationRow = createPropRow('rotation');
  // Rotation is stored in radians; the 'angle' dragger displays/edits in degrees.
  const setRotation = (value: number) => {
    if (node.kind === 'frame') setFramePropertyAndRender(deps, node.id, 'rotation', value);
    else setInstancePropertyAndRender(deps, node.id, 'rotation', value);
  };
  rotationRow.appendChild(createNumberInput(deps, node.rotation, -TWO_PI, TWO_PI, setRotation, DEG2RAD, `Set ${node.kind} rotation`, { subtype: 'angle' }));
  panel.appendChild(rotationRow);

  const scaleRow = createPropRow('scale');
  const setScale = (value: typeof node.scale) => {
    if (node.kind === 'frame') setFramePropertyAndRender(deps, node.id, 'scale', value);
    else setInstancePropertyAndRender(deps, node.id, 'scale', value);
  };
  scaleRow.appendChild(createNumberTupleInput(deps, node.scale, VEC2_COMPONENTS, setScale, {
    min: Number.NEGATIVE_INFINITY,
    max: Number.POSITIVE_INFINITY,
    step: 0.1,
    historyLabel: `Set ${node.kind} scale`,
    visual: { subtype: 'factor' },
  }));
  panel.appendChild(scaleRow);

  const originRow = createPropRow('origin');
  const setOrigin = (value: typeof node.origin) => {
    if (node.kind === 'frame') setFramePropertyAndRender(deps, node.id, 'origin', value);
    else setInstancePropertyAndRender(deps, node.id, 'origin', value);
  };
  originRow.appendChild(createNumberTupleInput(deps, node.origin, VEC2_COMPONENTS, setOrigin, {
    min: 0,
    max: 1,
    step: 0.01,
    historyLabel: `Set ${node.kind} origin`,
    visual: { showProgress: true, subtype: 'percentage' },
  }));
  panel.appendChild(originRow);

  const visRow = createPropRow('visible');
  const visCheck = document.createElement('input');
  visCheck.type = 'checkbox';
  visCheck.checked = node.visible;
  visCheck.addEventListener('change', () => {
    const next = visCheck.checked;
    if (node.visible === next) return;
    if (node.kind === 'frame') setFramePropertyAndRender(deps, node.id, 'visible', next);
    else setInstancePropertyAndRender(deps, node.id, 'visible', next);
  });
  visRow.appendChild(createCheckboxField(visCheck));
  panel.appendChild(visRow);
}
